import dayjs from 'dayjs'

/** One saved memory, as chat's `GET /memories` returns it (chat#258). */
export type Memory = {
  id: string
  content: string
  category: string
  subject: string | null
  created_at: string
  last_used_at: string | null
}

export type MemoryGroup = {
  category: string
  label: string
  memories: Memory[]
}

export const defaultMemory = (): Memory => ({
  id: 'memory-1',
  content: 'A full day is 34 man-hours',
  category: 'estimating',
  subject: null,
  created_at: '2026-10-20T00:00:00Z',
  last_used_at: null,
})

// Display order and labels only. Chat owns which categories exist, so one this
// list doesn't know still shows, after these, under a label made from its id.
const CATEGORY_LABELS: Record<string, string> = {
  estimating: 'Estimating',
  products: 'Products & materials',
  writing: 'Writing',
  sales_defaults: 'Sales defaults',
  business: 'Your business',
  interaction: 'Working with you',
  job_costing: 'Job costing',
}
const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS)

export function categoryLabel(category: string): string {
  const known = CATEGORY_LABELS[category]
  if (known) {
    return known
  }
  const words = category.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function categoryRank(category: string): number {
  const at = CATEGORY_ORDER.indexOf(category)
  return at === -1 ? CATEGORY_ORDER.length : at
}

/** Memories by category, known categories first; each group keeps chat's order. */
export function groupMemories(memories: Memory[]): MemoryGroup[] {
  const byCategory = new Map<string, Memory[]>()
  for (const memory of memories) {
    byCategory.set(memory.category, [...(byCategory.get(memory.category) ?? []), memory])
  }
  return [...byCategory.entries()]
    .sort(([a], [b]) => categoryRank(a) - categoryRank(b) || a.localeCompare(b))
    .map(([category, grouped]) => ({ category, label: categoryLabel(category), memories: grouped }))
}

const DATE_FORMAT = 'MMM D, YYYY'

export function memoryDates(memory: Memory): { saved: string; lastUsed: string } {
  return {
    saved: `Saved ${dayjs(memory.created_at).format(DATE_FORMAT)}`,
    lastUsed: memory.last_used_at
      ? `Last used ${dayjs(memory.last_used_at).format(DATE_FORMAT)}`
      : 'Not used yet',
  }
}
